import React from 'react';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import css from './radio-group.module.css';
import inputCss from './read-input.module.css';
import FormField from '../../common/form-field';
import { parseError } from '../../../services/utils';

export default function RadioGroup(
  name: string,
  buttons: {
    value?: string | boolean;
    label?: string;
  }[],
  label: string = null,
  column?: boolean
): FormComponent {
  return ({ ...rest }) => (
    <RadioGroupComponent
      name={name}
      buttons={buttons}
      label={label}
      column={column}
      {...rest}
    />
  );
}

export function RadioGroupComponent({
  name,
  label,
  buttons,
  parentName,
  disabled,
  readonly,
  required,
  column,
}: FormComponentProps & {
  name: string;
  label: string;
  buttons: {
    value?: string | boolean;
    label?: string;
  }[];
  required?: string[];
  column?: boolean;
}) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const radioName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(radioName, errors);

  if (readonly) {
    return (
      <FormField label={t(label)} labelFor={radioName} column={column}>
        <Controller
          name={name}
          defaultValue={null}
          render={({ field: { value } }) => {
            const selectedButton = buttons.find((btn) => btn.value === value);
            return (
              <input
                readOnly={true}
                type="text"
                className={inputCss['readonly']}
                value={selectedButton?.label ?? ''}
                name={name}
              />
            );
          }}
        />
      </FormField>
    );
  } else {
    return (
      <FormField
        labelFor={radioName}
        column={column}
        label={label ? t(label) : null}
        required={required?.includes(radioName)}
        error={error ? error.message : null}
      >
        <div className={css['radio-container']}>
          {buttons.map(
            (
              button: { value?: string | boolean; label?: string },
              index: number
            ) => (
              <span className={css['option']} key={index}>
                <input
                  type="radio"
                  id={`${radioName}-${button.value}`}
                  name={radioName}
                  value={button.value.toString()}
                  disabled={disabled}
                  readOnly={readonly}
                  className={css['radio']}
                  {...register(radioName)}
                />
                <label htmlFor={`${radioName}-${button.value}`}>
                  {t(button.label)}
                </label>
              </span>
            )
          )}
        </div>
      </FormField>
    );
  }
}
